/**
 * Notifications the API keeps for an account (user_notification), as opposed
 * to the app's own log in store/notifications. Only types this release knows
 * are parsed; anything newer is skipped.
 */

export interface GiveawayEntryNotification {
  kind: 'giveaway_entry'
  id: number
  at: Date
  giveawayId: number
  giveawayName: string
  giveawaySlug: string | null
  username: string
}

/** You won a giveaway's draw. */
export interface GiveawayWonNotification {
  kind: 'giveaway_won'
  id: number
  at: Date
  giveawayId: number
  giveawayName: string
  giveawaySlug: string | null
  /** 'key': it's on your loot page. 'trade': the organiser hands it over. */
  prizeKind: 'key' | 'trade'
}

/** One of your giveaways ended (winners drawn, if it was a draw). */
export interface GiveawayEndedNotification {
  kind: 'giveaway_ended'
  id: number
  at: Date
  giveawayId: number
  giveawayName: string
  giveawaySlug: string | null
  winners: number
}

export type AccountNotification =
  | GiveawayEntryNotification
  | GiveawayWonNotification
  | GiveawayEndedNotification

export function parseAccountNotification(raw: unknown): AccountNotification | null {
  if (typeof raw !== 'object' || raw === null) return null
  const n = raw as Record<string, unknown>
  const data = (typeof n.data === 'object' && n.data !== null ? n.data : {}) as Record<
    string,
    unknown
  >
  if (typeof n.id !== 'number') return null

  if (n.type === 'giveaway_entry' && typeof data.giveaway_id === 'number') {
    return {
      kind: 'giveaway_entry',
      id: n.id,
      at: new Date(typeof n.created_at === 'string' ? n.created_at : Date.now()),
      giveawayId: data.giveaway_id,
      giveawayName:
        typeof data.giveaway_name === 'string' && data.giveaway_name
          ? data.giveaway_name
          : 'your giveaway',
      giveawaySlug:
        typeof data.giveaway_slug === 'string' && data.giveaway_slug ? data.giveaway_slug : null,
      username: typeof data.username === 'string' && data.username ? data.username : 'Someone',
    }
  }

  if (n.type === 'giveaway_won' && typeof data.giveaway_id === 'number') {
    return {
      kind: 'giveaway_won',
      id: n.id,
      at: new Date(typeof n.created_at === 'string' ? n.created_at : Date.now()),
      giveawayId: data.giveaway_id,
      giveawayName:
        typeof data.giveaway_name === 'string' && data.giveaway_name
          ? data.giveaway_name
          : 'a giveaway',
      giveawaySlug:
        typeof data.giveaway_slug === 'string' && data.giveaway_slug ? data.giveaway_slug : null,
      prizeKind: data.prize_kind === 'trade' ? 'trade' : 'key',
    }
  }

  if (n.type === 'giveaway_ended' && typeof data.giveaway_id === 'number') {
    return {
      kind: 'giveaway_ended',
      id: n.id,
      at: new Date(typeof n.created_at === 'string' ? n.created_at : Date.now()),
      giveawayId: data.giveaway_id,
      giveawayName:
        typeof data.giveaway_name === 'string' && data.giveaway_name
          ? data.giveaway_name
          : 'Your giveaway',
      giveawaySlug:
        typeof data.giveaway_slug === 'string' && data.giveaway_slug ? data.giveaway_slug : null,
      winners: typeof data.winners === 'number' ? data.winners : 0,
    }
  }
  return null
}

export const giveawayPathOf = (n: { giveawaySlug: string | null; giveawayId: number }) =>
  `/g/${n.giveawaySlug ?? n.giveawayId}`

/** What to tell an owner whose giveaway just ended. */
export function describeEnded(n: GiveawayEndedNotification): string {
  if (n.winners === 0) return `${n.giveawayName} has ended with no winners.`
  return `${n.giveawayName} has ended: ${n.winners} ${n.winners === 1 ? 'winner' : 'winners'}.`
}

/**
 * What to tell a winner. A key is waiting on their loot page; a trade prize
 * comes from the organiser, so that one points at the giveaway instead.
 */
export function describeWin(n: GiveawayWonNotification): {
  message: string
  action: { label: string; path: string }
} {
  return n.prizeKind === 'trade'
    ? {
        message: `You won ${n.giveawayName}! The organiser will be in touch to hand over your prize.`,
        action: { label: 'View giveaway', path: giveawayPathOf(n) },
      }
    : {
        message: `You won ${n.giveawayName}! Your key is waiting in My Loot.`,
        action: { label: 'Open My Loot', path: '/my/loot' },
      }
}

/**
 * Wins from the last two weeks, for a device that has never caught up
 * before. Other notifications start from "now" there, but a win is worth
 * hearing about even on a new device.
 */
export const WIN_CATCH_UP_MS = 14 * 24 * 60 * 60 * 1000

/**
 * What to tell the owner about a batch of entries, one message per giveaway:
 * "deen entered Blood Banner." or "3 people entered Blood Banner."
 */
export function summarizeEntries(
  items: GiveawayEntryNotification[],
): { message: string; path: string; giveawayId: number }[] {
  const byGiveaway = new Map<number, GiveawayEntryNotification[]>()
  for (const item of items) {
    const group = byGiveaway.get(item.giveawayId)
    if (group) group.push(item)
    else byGiveaway.set(item.giveawayId, [item])
  }
  return [...byGiveaway.values()].map((group) => {
    const latest = group[group.length - 1]
    const who = group.length === 1 ? latest.username : `${group.length} people`
    return {
      message: `${who} entered ${latest.giveawayName}.`,
      path: giveawayPathOf(latest),
      giveawayId: latest.giveawayId,
    }
  })
}
